import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from '../../../lib/errors';
import { rpaRepository as repo } from '../../../lib/repositories/rpa.repository';
import type {
  ChiTietLoi,
  CodeLoiDayDu,
  MucCho,
  ThanChiTietLoi,
  ThanCodeLoi,
  TongQuanCodeLoi,
  TrangThaiCho,
} from '../types';
import { KHOA_RPA, thuLai } from './useRpa';

export function useTongQuanCode(bat = true) {
  return useQuery<TongQuanCodeLoi, ApiError>({
    queryKey: KHOA_RPA.tongQuanCode,
    queryFn: () => repo.tongQuanCode(),
    enabled: bat,
    staleTime: 15_000,
    retry: thuLai,
  });
}

export function useCodeLoi(code: string) {
  return useQuery<CodeLoiDayDu, ApiError>({
    queryKey: KHOA_RPA.code(code),
    queryFn: () => repo.code(code),
    enabled: code !== '',
    staleTime: 15_000,
    retry: thuLai,
  });
}

export function useMucCho(status: TrangThaiCho, bat = true) {
  return useQuery<MucCho[], ApiError>({
    queryKey: KHOA_RPA.cho(status),
    queryFn: () => repo.danhSachCho(status),
    enabled: bat,
    staleTime: 15_000,
    retry: thuLai,
  });
}

export function usePhanLoai() {
  return useQuery<string[], ApiError>({
    queryKey: KHOA_RPA.phanLoai,
    queryFn: () => repo.phanLoai(),
    staleTime: 60_000,
    retry: thuLai,
  });
}

/**
 * Mọi thao tác ghi làm mới cả ba bảng (code, chi tiết, chờ) và danh sách phân loại — kể cả khi hỏng: gán một mục chờ
 * vừa bị người khác gán trước (`409`) thì danh sách đang hiện đã cũ.
 */
function useLamMoiLoi() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: KHOA_RPA.loi });
}

export function useTaoCode() {
  const lamMoi = useLamMoiLoi();
  return useMutation<CodeLoiDayDu, ApiError, ThanCodeLoi & { pendingId?: string }>({
    mutationFn: (than) => repo.taoCode(than),
    onSettled: lamMoi,
  });
}

export function useSuaCode() {
  const lamMoi = useLamMoiLoi();
  return useMutation<CodeLoiDayDu, ApiError, { code: string; than: Partial<ThanCodeLoi> }>({
    mutationFn: ({ code, than }) => repo.suaCode(code, than),
    onSettled: lamMoi,
  });
}

export function useXoaCode() {
  const lamMoi = useLamMoiLoi();
  return useMutation<void, ApiError, string>({ mutationFn: (code) => repo.xoaCode(code), onSettled: lamMoi });
}

export function useThemChiTiet() {
  const lamMoi = useLamMoiLoi();
  return useMutation<ChiTietLoi, ApiError, { code: string; than: ThanChiTietLoi }>({
    mutationFn: ({ code, than }) => repo.themChiTiet(code, than),
    onSettled: lamMoi,
  });
}

export function useSuaChiTiet() {
  const lamMoi = useLamMoiLoi();
  return useMutation<ChiTietLoi, ApiError, { id: string; than: Partial<ThanChiTietLoi> }>({
    mutationFn: ({ id, than }) => repo.suaChiTiet(id, than),
    onSettled: lamMoi,
  });
}

export function useXoaChiTiet() {
  const lamMoi = useLamMoiLoi();
  return useMutation<void, ApiError, string>({ mutationFn: (id) => repo.xoaChiTiet(id), onSettled: lamMoi });
}

export function useGanCho() {
  const lamMoi = useLamMoiLoi();
  return useMutation<CodeLoiDayDu, ApiError, { id: string; code: string }>({
    mutationFn: ({ id, code }) => repo.ganCho(id, code),
    onSettled: lamMoi,
  });
}

export function useBoQuaCho() {
  const lamMoi = useLamMoiLoi();
  return useMutation<MucCho, ApiError, string>({ mutationFn: (id) => repo.boQuaCho(id), onSettled: lamMoi });
}
